import React, { useEffect, useState } from 'react'
import './RentalContractsPage.css'
import { useDispatch, useSelector } from 'react-redux'
import MenuBar from '../../components/MenuBar'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../../components/placeholders/LoadingSpinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import httpClient from '../../components/helpers/httpClient'
import Label from '../../components/Label'
import { formatDate } from '../../components/helpers/formatDate'
import { updateFilters } from '../../store/rentalContractsPage'
import DropDownFilter from '../../components/DropDownFilter'
import Slider from '../../components/Slider'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ExportButton from '../../components/UI/ExportButton.jsx'
import { EXPORT_LIMIT, flattenExportRows } from '../../components/helpers/exportUtils'
import { rentalContractTypeLabelColors, rentalContractTypes } from '../../assets/data/data'

function RentalContractsPage() {
  const [contracts, setContracts] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(25)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const filters = useSelector(state => state.rentalContractsPage.filters)

  const fetchContracts = async () => {
    setLoading(true)
    const params = {
      page: filters.page,
      limit: limit,
      detailed: true,
    }
    if (filters.searchWord && filters.searchWord.trim() !== '') params.search = filters.searchWord.trim()
    if (filters.typeFilter && filters.typeFilter !== '') params.rental_type = filters.typeFilter
    if (typeof filters.activeOnly === 'boolean') params.active = filters.activeOnly ? 'true' : 'false'

    const queryParams = new URLSearchParams(params).toString()

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/rental-contracts?${queryParams}`)
    setContracts(response.data.rental_contracts || [])
    setTotalPages(response.data.total_pages || 0)
    setLoading(false)
  }

  const prepareExportData = async () => {
    const params = {
      page: 1,
      limit: EXPORT_LIMIT,
      detailed: true,
    }

    if (filters.searchWord && filters.searchWord.trim() !== '') params.search = filters.searchWord.trim()
    if (filters.typeFilter && filters.typeFilter !== '') params.rental_type = filters.typeFilter
    if (typeof filters.activeOnly === 'boolean') params.active = filters.activeOnly ? 'true' : 'false'

    const queryParams = new URLSearchParams(params).toString()
    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/rental-contracts?${queryParams}`)

    return flattenExportRows(response.data?.rental_contracts || [])
  }

  useEffect(() => {
    fetchContracts()
  }, [])

  useEffect(() => {
    fetchContracts()
  }, [filters])

  const handleOnClickRow = (contractId) => {
    navigate(`/rental-contracts/${contractId}`)
  }

  const handlePagination = (event) => {
    const element = event.currentTarget
    if (element.classList.contains('grayed-out')) return
    const type = element.dataset.type
    const updated = { ...filters }
    switch (type) {
      case 'previous':
        updated.page = filters.page - 1
        dispatch(updateFilters(updated))
        break
      case 'next':
        updated.page = filters.page + 1
        dispatch(updateFilters(updated))
        break
    }
  }

  const handleSearchWord = (event) => {
    const updated = { ...filters, searchWord: event.target.value, page: 1 }
    dispatch(updateFilters(updated))
  }

  const handleClearSearchBar = () => {
    if (!filters.searchWord) return
    const updated = { ...filters, searchWord: '', page: 1 }
    dispatch(updateFilters(updated))
  }

  const handleOnClickDropdownItem = (e, value, idList) => {
    const text = typeof value === 'object' ? (value.name || '') : (value || '')
    const newFilters = { ...filters, typeFilter: text, page: 1 }
    dispatch(updateFilters(newFilters))
  }

  const typeOptions = rentalContractTypes

  return (
    <div className='template'>
      <Header>
        <div className="header__actions--left">
          <ExportButton
            data={prepareExportData}
            filename="rental_contracts_export"
            formats={['excel']}
            disabled={loading}
          />
        </div>

        <div className="header__filters" id="rental-contracts__table-btns">
          <div className="search-bar" id="rental-contracts__search-bar--table" onClick={handleClearSearchBar}>
            <input
              placeholder="Search id, unit, project..."
              value={filters.searchWord || ''}
              onChange={handleSearchWord}
            />
            {(filters.searchWord || '') === '' ? <SearchIcon /> : <ClearIcon />}
          </div>
          <DropDownFilter
            idList="rental_contracts_filter--type"
            placeholderText="Filter on type"
            listArray={typeOptions}
            onClickItem={handleOnClickDropdownItem}
            filters={filters}
            value={filters.typeFilter || ''}
            filter={"typeFilter"}
            setValue={updateFilters}
          />
          <div className="hide-inactive-filter">
            <Slider
              text="Is active"
              defaultState={filters.activeOnly === true}
              state={filters.activeOnly === true}
              size={1.2}
              fontSize="0.8rem"
              onClickFunction={(id, newState) => {
                const updated = { ...filters, activeOnly: newState, page: 1 }
                dispatch(updateFilters(updated))
              }}
            />
          </div>
        </div>
      </Header>

      <MenuBar />

      <div className="template__body" id='body-rental-contracts'>
        <button
          className="floating__button--bottom-right"
          id="add--btn"
          onClick={() => navigate('/rental-contracts/new')}
        />

        <div className={`body__section`} id='all-rental-contracts-table'>
          <div className='table'>
            <div className='table__header-5'>
              <div className='table__header--field'>Contract number</div>
              <div className='table__header--field'>Contract start date</div>
              <div className='table__header--field'>Unit(s)</div>
              <div className='table__header--field'>Project(s)</div>
              <div className='table__header--field'>Type</div>
            </div>
            <div className='table__rows_container'>
              {loading ? (
                <LoadingSpinner />
              ) : (
                contracts.map((c, index) => {
                  const unitNames = Array.isArray(c.units) ? c.units.map(u => u.unit_name) : []
                  const projectNames = Array.isArray(c.units) ? [...new Set(c.units.map(u => u.building_id?.project_id?.project_commercial_name).filter(Boolean))] : []
                  return (
                    <div
                      className={`table__row-5 ${c.active === false ? 'table__row--inactive' : ''}`}
                      key={index}
                      onClick={() => handleOnClickRow(c.id)}
                      data-contract-id={c.id}
                    >
                      <div className='table__row--field rental-contract-id-cell'>
                        <span>{c.id || 'N/A'}</span>
                        {c.active === false ? <span className='rental-contract-status'>Inactive</span> : null}
                      </div>
                      <div className='table__row--field'>{c.start_date ? formatDate(c.start_date) : 'N/A'}</div>
                      <div className='table__row--field'>
                        {unitNames.length > 0 && (
                          <div className='labels-container'>
                            {unitNames.map((name, i) => (
                              <span key={i} className='label-item'>{name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='table__row--field'>
                        {projectNames.length > 0 && (
                          <div className='labels-container'>
                            {projectNames.map((name, i) => (
                              <span key={i} className='label-item'>{name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className='table__row--field'>
                        {c.rental_type ? (
                          <Label
                            title={c.rental_type}
                            labelColor={rentalContractTypeLabelColors[c.rental_type] || 'gray'}
                          />
                        ) : null}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <div className="pagination">
              <ArrowBackIosIcon
                className={`pagination__btn ${filters.page === 1 ? 'grayed-out' : ''}`}
                onClick={handlePagination}
                data-type="previous"
                id="prevPage"
              />
              <ArrowForwardIosIcon
                className={`pagination__btn ${filters.page === totalPages ? 'grayed-out' : ''}`}
                onClick={handlePagination}
                data-type="next"
                id="nextPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RentalContractsPage


